// src/components/auth/PrivateRoute.tsx
// Guards routes — redirects to /login if not authenticated
// Shows a spinner while the app is initializing (silent refresh on load)

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ListTodo } from 'lucide-react';
import { RootState } from 'store';

interface Props {
  children: React.ReactElement;
}

export default function PrivateRoute({ children }: Props) {
  const { isAuthenticated, isInitializing, user } = useSelector((s: RootState) => s.auth);
  const location = useLocation();

  // Still doing silent refresh on app mount — show nothing / spinner
  if (isInitializing) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--om-app)]">
        <div className="text-center">
          <div className="flex justify-center mb-3"><ListTodo className="w-7 h-7" strokeWidth={2} /></div>
          <div className="text-[var(--om-text-dim)] text-xs">Loading...</div>
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to login, preserve intended destination
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin users should only see the admin dashboard, not the main app
  if (user?.isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}
